import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import PublicNavbar from "../components/layout/PublicNavbar";
import DebtNavbar from "../components/layout/DebtNavbar";
import { Button, Input } from "../components/common/Primitives";
import { useAuth } from "../lib/AuthContext";
import { formatApiError } from "../lib/api";
import Seo from "../components/Seo";
import { Rocket, HandCoins, TrendingUp, ArrowLeft } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

/**
 * Intent-aware signup surface.
 *
 * `/signup`                    -> AuthChooser (mode=signup)
 * `/for-startups/signup`       -> intent="equity"   -> creates role=startup
 * `/money/signup`         -> intent="debt"     -> creates role=startup (borrower shares the model)
 * `/for-investors/signup`      -> intent="investor" -> creates role=investor
 *
 * Post-signup redirects:
 *  - equity   -> /startup/profile
 *  - debt     -> /raise-debt?prefill=1  (drop them straight on the lead form)
 *  - investor -> /investor/preferences
 */
export default function Signup({ intent = "equity" }) {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", mobile: "" });
  const [loading, setLoading] = useState(false);

  const isDebt = intent === "debt";
  const isInvestor = intent === "investor";
  const role = isInvestor ? "investor" : "startup"; // debt borrowers use the startup role

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, role });
      toast.success("Account created.");
      try {
        const backend = process.env.REACT_APP_BACKEND_URL;
        fetch(`${backend}/api/hero-copy/convert`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ kind: "signup" }),
        });
      } catch { /* noop */ }
      if (isInvestor) nav("/investor/preferences");
      else if (isDebt) nav("/money/onboard");
      else nav("/startup/profile");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    try {
      sessionStorage.setItem("venturaz_google_role", role);
      sessionStorage.setItem("venturaz_pending_login_intent", intent);
    } catch { /* ignore */ }
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${backend}/api/auth/google/init?redirect=${encodeURIComponent("/")}&role=${encodeURIComponent(role)}`;
  };

  const themeCfg = isDebt ? {
    heroClass: "bg-gradient-to-br from-[#FBFAF5] via-[#F5F1E8] to-[#E8DFC9]",
    overline: "VENTURAZ MONEY · JOIN",
    heading: "Create your borrower account.",
    sub: "One profile — compare offers from 85+ banks & NBFCs. Free CIBIL worth ₹999.",
    Icon: HandCoins,
    submitLabel: "Create Borrower Account",
    panelExtra: "border-t-4 border-[#B8860B]",
    otherCta: [
      { label: "I'm raising equity", to: "/for-startups/signup" },
      { label: "I invest in startups", to: "/for-investors/signup" },
    ],
    textAccent: "text-ink",
    subText: "text-ink-muted",
    crosslinkClass: "text-[#7A4E1D]",
    backLinkClass: "text-[#7A4E1D]",
  } : isInvestor ? {
    heroClass: "liquid-morph-hero",
    overline: "VENTURAZ · INVESTOR JOIN",
    heading: "Create your investor account.",
    sub: "Access hand-picked deal flow. VenturaZ Handpicked ribbon on curated deals.",
    Icon: TrendingUp,
    submitLabel: "Create Investor Account",
    panelExtra: "",
    otherCta: [
      { label: "I'm raising equity", to: "/for-startups/signup" },
      { label: "I need a loan", to: "/money/signup" },
    ],
    textAccent: "text-cream",
    subText: "text-cream/70",
    crosslinkClass: "text-cream/80 hover:text-gold",
    backLinkClass: "text-cream/80 hover:text-gold",
  } : {
    heroClass: "liquid-morph-hero",
    overline: "VENTURAZ · FOUNDER JOIN",
    heading: "Create your founder account.",
    sub: "AI-matched to family offices, funds, and strategics. Trust-scored profiles.",
    Icon: Rocket,
    submitLabel: "Create Founder Account",
    panelExtra: "",
    otherCta: [
      { label: "I need a loan", to: "/money/signup" },
      { label: "I invest in startups", to: "/for-investors/signup" },
    ],
    textAccent: "text-cream",
    subText: "text-cream/70",
    crosslinkClass: "text-cream/80 hover:text-gold",
    backLinkClass: "text-cream/80 hover:text-gold",
  };
  const { Icon } = themeCfg;

  const NavBar = isDebt ? DebtNavbar : PublicNavbar;

  return (
    <div className={`min-h-screen relative ${isDebt ? "text-ink debt-shell" : "text-cream"} ${themeCfg.heroClass} !border-0 !shadow-none !rounded-none`}>
      <Seo
        title={`Join VenturaZ (${intent})`}
        description="Sign up for VenturaZ — pick your surface and get onboarded in under 60 seconds."
        path={`/signup/${intent}`}
      />
      <Toaster richColors position="top-right" />
      <NavBar />
      <section className="relative max-w-md mx-auto px-6 py-14 lg:py-20">
        <Link to="/signup" className={`inline-flex items-center gap-1 text-xs mb-4 hover:underline ${themeCfg.backLinkClass}`} data-testid="signup-back-to-chooser">
          <ArrowLeft size={12} /> Different surface? Change
        </Link>
        <div className={`overline mb-3 ${isDebt ? "text-[#7A4E1D]" : "text-gold/90"}`} data-testid="signup-overline">{themeCfg.overline}</div>
        <div className="flex items-center gap-3 mb-6">
          <div className={`w-10 h-10 rounded-lg inline-flex items-center justify-center ${isDebt ? "bg-[#7A4E1D] text-[#FFF3D9]" : "bg-gold text-navy"}`}>
            <Icon size={18} />
          </div>
          <div>
            <h1 className={`font-display text-2xl leading-tight ${themeCfg.textAccent}`} data-testid="signup-heading">{themeCfg.heading}</h1>
            <div className={`text-xs mt-0.5 ${themeCfg.subText}`}>{themeCfg.sub}</div>
          </div>
        </div>
        <form
          onSubmit={submit}
          className={`space-y-4 liquid-morph-panel rounded-2xl p-6 text-ink ${themeCfg.panelExtra}`}
          data-testid="signup-form"
        >
          <Input label="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required testId="signup-name" />
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required testId="signup-email" />
          <Input label="Mobile (Optional)" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} testId="signup-mobile" />
          <Input label="Password (min 6)" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} testId="signup-password" />
          <Button type="submit" disabled={loading} className="w-full" data-testid="signup-submit-btn">
            {loading ? "Creating…" : themeCfg.submitLabel}
          </Button>
          <div className="flex items-center gap-3 text-xs text-ink-soft">
            <div className="flex-1 h-px bg-navy/15" /> OR <div className="flex-1 h-px bg-navy/15" />
          </div>
          <button type="button" onClick={google} className="w-full py-2.5 border border-navy/20 text-ink-soft font-heading text-sm hover:border-[#8A6600] hover:text-[#8A6600] transition-colors" data-testid="signup-google-btn">
            Continue with Google
          </button>
          <div className="text-sm text-ink-soft text-center pt-2">
            Already have an account?{" "}
            <Link
              to={isDebt ? "/money/login" : isInvestor ? "/for-investors/login" : "/for-startups/login"}
              className="text-[#8A6600] font-semibold hover:underline"
              data-testid="signup-login-link"
            >Sign in →</Link>
          </div>
        </form>

        <div className="mt-6 text-xs text-center">
          {themeCfg.otherCta.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className={`mx-2 inline-block hover:underline ${themeCfg.crosslinkClass}`}
              data-testid={`signup-crosslink-${c.to.replace(/\//g, "-").slice(1)}`}
            >
              {c.label} →
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
